import React, { useEffect, useId, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'
import type { FormErrors } from '@ztechapps/webui.form'
import { Field, Form } from '@ztechapps/webui.form'
import { PhoneNumberInput, TextAreaInput, TextInput } from '@ztechapps/webui.input'
import { getWindow, isValidEmail, isValidPhoneNumber } from '@ztechapps/common.utils'
import { useIsMobileScreenSize } from '@ztechapps/common.hook'
import { useKeyboardAwareHeight } from '@ztechapps/ai.webui'
import { chatAttachmentMaxBytes, storefrontAttachmentMaxCount } from '@ztechapps/chat.common'
import { ConversationWidgetAttachments } from '@ztechapps/chat.webui'
import { Alert, HorizontalStack, Icon, ShowIf, Spinner } from '@ztechapps/webui.util'
import { BusyButton, SaveCancelButtons } from '@ztechapps/webui.button'
import { appSettings } from '../../config/settings'
import { useUser } from '../../hooks/useUser'
import { useCustomer } from '../../hooks/useCustomer'
import { useConversationWidgetContext } from './Context'
import styles from './ConversationWidgetPanel.module.scss'

// Path of the public, unauthenticated conversation endpoint on the API. The
// browser posts Direct (cross-origin) at the API, so there is no forwarding
// hop and no signing.
// COUPLING: must match the public router's path in
// apps/panelpunches/api/src/config/bootstrap/router/, mounted by that app's
// RouteBootstrap.
const PUBLIC_CONVERSATION_PATH = '/storefront-conversations'

// Sent as `contactReason` when the decoy checkbox is ticked. The scorer only
// tests the field non-blank, so any non-empty string works — this one reads
// sensibly in a captured payload.
// COUPLING: the API's scorer maps this field onto its decoy signal; a rename
// here must rename that mapping too.
const DECOY_TICKED_VALUE = 'yes'

const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.'
const NETWORK_ERROR_MESSAGE = 'Could not reach the server. Check your connection and try again.'

type ConversationWidgetValues = {
  name: string
  email: string
  company: string
  phone: string
  message: string
  attachments: File[]
}

const EMPTY_FORM: ConversationWidgetValues = {
  name: '',
  email: '',
  company: '',
  phone: '',
  message: '',
  attachments: [],
}

export const ConversationWidgetPanel = () => {
  const { isOpen, closeConversationWidget } = useConversationWidgetContext()
  const { loggedIn, accessToken } = useUser()
  const { document: customer, loading } = useCustomer()
  const [serverError, setServerError] = useState<string | null>(null)
  const decoyRef = useRef<HTMLInputElement>(null)
  const dwellStartRef = useRef<number | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const isPhone = useIsMobileScreenSize()
  const keyboardAwareHeight = useKeyboardAwareHeight('100%')

  // A signed-in visitor's identity comes from their session, so the form
  // starts from their stored profile instead of a blank slate.
  const initialValues: ConversationWidgetValues = loggedIn
    ? {
        ...EMPTY_FORM,
        name: customer?.profile?.name ?? '',
        email: customer?.profile?.email ?? '',
        company: customer?.company?.name ?? '',
        phone: customer?.phone ?? '',
      }
    : EMPTY_FORM

  // The scorer reads how long the visitor spent on the form. The clock starts
  // when a field first takes focus and is read at submit; a submit with no
  // focus reports null, which the scorer reads as its own no-dwell signal. It
  // rides on a focus wrapper rather than a Field `onChange` function so the
  // shared Field keeps its default value wiring untouched.
  const startDwellOnFirstFocus = () => {
    if (dwellStartRef.current === null) dwellStartRef.current = Date.now()
  }

  const validate = (values: ConversationWidgetValues): Partial<FormErrors<ConversationWidgetValues>> => {
    const errors: Partial<FormErrors<ConversationWidgetValues>> = {}
    if (isEmpty(values.name.trim())) errors.name = 'Please enter your name.'
    if (isEmpty(values.message.trim())) errors.message = 'Please enter a message.'
    if (!loggedIn) {
      if (isEmpty(values.email.trim())) errors.email = 'Please enter your email address.'
      else if (!isValidEmail(values.email.trim())) errors.email = 'Please enter a valid email address.'
    }
    if (!isValidPhoneNumber(values.phone)) errors.phone = 'Please enter a valid phone number.'
    if (values.attachments.length > storefrontAttachmentMaxCount)
      errors.attachments = `Up to ${storefrontAttachmentMaxCount} files allowed.`
    else if (values.attachments.some((file) => file.size > chatAttachmentMaxBytes))
      errors.attachments = 'Each file must be 10 MB or smaller.'
    return errors
  }

  const resetAuxState = () => {
    setServerError(null)
    if (decoyRef.current) decoyRef.current.checked = false
    dwellStartRef.current = null
  }

  const handleSubmit = async (values: ConversationWidgetValues, formikBag: { resetForm: () => void }) => {
    setServerError(null)

    const apiBase = appSettings.AppUrl.api
    if (!apiBase) {
      setServerError(GENERIC_ERROR_MESSAGE)
      return
    }

    try {
      const contactReason = decoyRef.current?.checked ? DECOY_TICKED_VALUE : ''
      const dwellMs = dwellStartRef.current === null ? null : Date.now() - dwellStartRef.current
      const formData = new FormData()
      formData.append('name', values.name.trim())
      formData.append('company', values.company.trim())
      formData.append('phone', values.phone.trim())
      formData.append('message', values.message.trim())
      // A signed-in customer is attributed from their session, so no address
      // is sent; an anonymous visitor has no session, so theirs is required.
      if (!loggedIn) formData.append('email', values.email.trim())
      formData.append('contactReason', contactReason)
      if (dwellMs !== null) formData.append('dwellMs', String(dwellMs))
      for (const file of values.attachments) formData.append('attachments', file)
      const headers: Record<string, string> = {}
      // Signed-in attribution rides on the login token; an anonymous visitor
      // has none to send, and the endpoint treats its absence as anonymous.
      // COUPLING: the API reads this header in
      // libs/node/core/src/router/RouterService.ts (`getAccessToken`).
      if (accessToken) headers['login-token'] = accessToken
      const response = await fetch(`${apiBase}${PUBLIC_CONVERSATION_PATH}`, {
        method: 'POST',
        headers,
        body: formData,
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result?.success) {
        setServerError(result?.error ?? GENERIC_ERROR_MESSAGE)
        return
      }

      formikBag.resetForm()
      resetAuxState()
      closeConversationWidget()
    } catch {
      setServerError(NETWORK_ERROR_MESSAGE)
    }
  }

  const minimizeOnEscape = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') closeConversationWidget()
  }

  // Land on the first field when the panel opens. The fields' own mount-time
  // autoFocus cannot fire inside a minimized panel, so this runs on open
  // instead; the panel itself takes focus while the signed-in spinner stands
  // in for the form.
  useEffect(() => {
    if (isOpen)
      (panelRef.current?.querySelector<HTMLElement>('input, textarea') ?? panelRef.current)?.focus({
        preventScroll: true,
      })
  }, [isOpen])

  // The phone panel covers the page, so its scroll is locked while open;
  // wider viewports keep the page scrollable behind the docked panel.
  useEffect(() => {
    if (!isPhone || !isOpen) return
    const win = getWindow()
    if (!win) return
    const originalOverflow = win.document.body.style.overflow
    win.document.body.style.overflow = 'hidden'
    return () => {
      win.document.body.style.overflow = originalOverflow
    }
  }, [isOpen, isPhone])

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={minimizeOnEscape}
      style={isPhone ? { height: keyboardAwareHeight } : undefined}
      className={`${isOpen ? 'd-flex' : 'd-none'} flex-column position-fixed overflow-hidden bg-body border shadow ${styles.panel}`}
    >
      <HorizontalStack className="px-3 py-2 border-bottom">
        <h2 id={titleId} className="fs-6 fw-semibold mb-0">
          Contact us
        </h2>
        <BusyButton
          variant="link"
          className="ms-auto p-0"
          icon={
            <span aria-hidden="true">
              <Icon name="minus" />
            </span>
          }
          aria-label="Minimize"
          onClick={closeConversationWidget}
        />
      </HorizontalStack>
      <div className="flex-grow-1 overflow-auto px-3 pt-3" data-testid="conversation-widget-body">
        <ShowIf condition={!isEmpty(serverError)}>
          <Alert type="danger">{serverError}</Alert>
        </ShowIf>
        <ShowIf condition={loggedIn && loading}>
          <Spinner />
        </ShowIf>
        <ShowIf condition={!(loggedIn && loading)}>
          <Form initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
            {({ isSubmitting, resetForm }) => (
              <div onFocus={startDwellOnFirstFocus}>
                <Field name="name" label="Name" placeholder="Your name" component={TextInput} />
                <Field
                  name="email"
                  label="Email"
                  placeholder="you@example.com"
                  component={TextInput}
                  type="email"
                  readOnly={loggedIn}
                  help="The reply will be sent to this address."
                />
                <Field name="company" label="Company (optional)" placeholder="Your company" component={TextInput} />
                <Field name="phone" label="Phone (optional)" placeholder="Your phone" component={PhoneNumberInput} />
                <Field
                  name="message"
                  label="How can we help you?"
                  placeholder="Your message"
                  component={TextAreaInput}
                  rows={4}
                />
                <Field
                  name="attachments"
                  label="Attachments (optional)"
                  help="Up to 5 files, 10 MB each — images, PDF, Office documents or DXF/DWG drawings."
                  component={ConversationWidgetAttachments}
                  onChange
                />
                <span aria-hidden="true" className={styles.decoyField}>
                  <input
                    name="contactReason"
                    type="checkbox"
                    ref={decoyRef}
                    defaultChecked={false}
                    tabIndex={-1}
                    autoComplete="off"
                    data-1p-ignore
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                    data-protonpass-ignore
                  />
                </span>
                <div className="position-sticky bottom-0 bg-body border-top py-2">
                  <SaveCancelButtons
                    saveType="submit"
                    saveText="Send message"
                    cancelType="button"
                    isBusy={isSubmitting}
                    disabled={isSubmitting}
                    onCancel={() => {
                      resetForm()
                      resetAuxState()
                      closeConversationWidget()
                    }}
                  />
                </div>
              </div>
            )}
          </Form>
        </ShowIf>
      </div>
    </div>
  )
}
